import React from 'react';
import PropTypes from 'prop-types';
import {get as _get, isNull as _isNull, isUndefined as _isUndefined} from 'lodash';
import {connect} from 'react-redux';
import {withRouter} from 'react-router';
import {compose} from 'redux';
import {Segment} from 'semantic-ui-react';

import {gql, graphql} from 'react-apollo';
import HeaderRenderer from '@appComponents/HeaderRenderer';
import {FilterQueryWrapper, FilterUrlParamsWrapper} from '@appComponents/HOCFiltersQueryWrapper';
import {getSearchFilters} from '@utils/filters';
import mapModulesToProps from '@utils/mapModulesToProps';
import navigation from '@constants/navigation';
import {
    PROPERTY_LICENCE_TYPE_OTHER,
    PROPERTY_LICENCE_TYPE_SPORTRADAR,
} from '@constants/variables';
import * as propertyLicenceEventsGraphQL from '@graphql/propertyLicences/events';
import {sortDropdownOptionsAlphanumerically} from '@utils/sorters';
import {showConstraintsModal, skipSearch} from '@modules/propertyLicence/utils/constraintsTables';
import {getTodayDate, getEndOfTodayDate} from '@utils/date';

import {exportExcel as propertyLicenceEventsExportExcel} from '../utils/export/propertyLicenceEventsExcel';
import PropertyLicenceEventsTable from '../components/PropertyLicenceEventsTable';
import PropertyLicenceEventsFilters from '../components/PropertyLicenceEventsFilters';

export class PropertyLicenceEvents extends React.Component {
    static propTypes = {
        DataPropertyLicenceEvents: PropTypes.object,
        DataPropertyLicenceEventsFilters: PropTypes.object,
        filters: PropTypes.shape({
            contentCategoriesLevel3: PropTypes.arrayOf(PropTypes.number),
            eventStatus: PropTypes.arrayOf(PropTypes.number),
            from: PropTypes.string,
            reportTypes: PropTypes.arrayOf(PropTypes.number),
            products: PropTypes.arrayOf(PropTypes.number),
            search: PropTypes.string,
            to: PropTypes.string,
            tournaments: PropTypes.arrayOf(PropTypes.number),
            incidentTypes: PropTypes.arrayOf(PropTypes.number),
            incidentSources: PropTypes.arrayOf(PropTypes.number),
        }),
        loadingRefetch: PropTypes.bool.isRequired,
        history: PropTypes.object.isRequired,
        match: PropTypes.object.isRequired,
        Menu: PropTypes.object.isRequired,
        propertyLicenceTypeId: PropTypes.number,
    };

    static defaultProps = {
        filters: {
            contentCategoriesLevel3: [],
            eventStatus: [],
            from: getTodayDate(),
            to: getEndOfTodayDate(),
            reportTypes: [],
            products: [],
            search: null,
            tournaments: [],
            incidentTypes: [],
            incidentSources: [],
        },
    };

    constructor() {
        super();

        this.state = {
            contentCategoriesLevel3: [],
            eventStatuses: [],
            reportTypes: [],
            products: [],
            propertyLicenceEvents: [],
            propertyLicenceTypeId: null,
            tournaments: [],
            incidentTypes: [],
            incidentSources: [],
        };
    }

    componentDidMount() {
        this.props.Menu.storeMenu(navigation.mediaRights.key);
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        if (_isNull(nextProps.propertyLicenceTypeId)) {
            return null;
        }

        const {
                DataPropertyLicenceEvents: {propertyLicenceEvents} = [],
                DataPropertyLicenceEventsFilters: {
                    propertyLicenceContents,
                    eventStatuses,
                    reportTypes,
                    products,
                    incidentTypes,
                    incidentSources,
                },
                propertyLicenceTypeId,
            } = nextProps,
            nextState = {};

        if (eventStatuses && prevState.eventStatuses !== eventStatuses) {
            nextState.eventStatuses = eventStatuses.map((eventStatus) => ({
                key: eventStatus.value,
                text: eventStatus.text.capitalizeFirstLetter(),
                value: parseInt(eventStatus.value, 10),
            }));
        }

        if (reportTypes && prevState.reportTypes !== reportTypes) {
            nextState.reportTypes = reportTypes.map((type) => ({
                key: type.value,
                text: type.text,
                value: parseInt(type.value, 10),
            }));
        }

        if (products && prevState.products !== products) {
            nextState.products = products.map((product) => ({
                key: product.value,
                text: product.text,
                value: parseInt(product.value, 10),
            }));
        }

        if (propertyLicenceContents) {
            nextState.contentCategoriesLevel3 = sortDropdownOptionsAlphanumerically(
                propertyLicenceContents.filter(
                    propertyLicenceContent => null !== propertyLicenceContent.content_category
                ).map((propertyLicenceContent) => ({
                    key: propertyLicenceContent.content_category.id,
                    text: `
                        ${propertyLicenceContent.content_category.parent.name}
                        >
                        ${propertyLicenceContent.content_category.name}
                    `,
                    value: parseInt(propertyLicenceContent.content_category.id, 10),
                }))
            );

            nextState.tournaments = sortDropdownOptionsAlphanumerically(
                propertyLicenceContents.filter(
                    propertyLicenceContent => null !== propertyLicenceContent.tournament
                ).map((propertyLicenceContent) => ({
                    key: propertyLicenceContent.tournament.id,
                    text: `
                        ${propertyLicenceContent.tournament.tournament_category.name}
                        >
                        ${propertyLicenceContent.tournament.name}
                    `,
                    value: parseInt(propertyLicenceContent.tournament.id, 10),
                }))
            );
        }

        if(skipSearch(nextProps)) {
            nextState.propertyLicenceEvents = [];
        } else if (propertyLicenceEvents && prevState.propertyLicenceEvents !== propertyLicenceEvents) {
            nextState.propertyLicenceEvents = propertyLicenceEvents;
        }

        if (!_isNull(propertyLicenceTypeId) && prevState.propertyLicenceTypeId !== propertyLicenceTypeId) {
            nextState.propertyLicenceTypeId = propertyLicenceTypeId;
        }

        if (incidentTypes) {
            nextState.incidentTypes = incidentTypes.map((type) => ({
                key: type.value,
                text: type.text,
                value: parseInt(type.value, 10),
            }));
        }

        if (incidentSources) {
            nextState.incidentSources = incidentSources.map((source) => ({
                key: source.value,
                text: source.text,
                value: parseInt(source.value, 10),
            }));
        }

        return nextState;
    }

    componentDidUpdate() {
        if (skipSearch(this.props)) {
            showConstraintsModal(this.props.filters);
        }
    }

    render() {
        if (_isNull(this.state.propertyLicenceTypeId)) {
            return <Segment basic loading={true}/>;
        }

        const loading = _get(this.props, 'DataPropertyLicenceEvents.loading') ||
                _get(this.props, 'DataPropertyLicenceEventsFilters.loading') ||
                this.props.loadingRefetch,
            filtersProps = {
                data: {
                    contentCategoriesLevel3: this.state.contentCategoriesLevel3,
                    eventStatuses: this.state.eventStatuses,
                    reportTypes: this.state.reportTypes,
                    products: this.state.products,
                    propertyLicenceTypeId: this.state.propertyLicenceTypeId,
                    tournaments: this.state.tournaments,
                    incidentTypes: this.state.incidentTypes,
                    incidentSources: this.state.incidentSources,
                },
                loading: loading,
            };

        let {id: propertyLicenceId} = this.props.match.params;

        return (
            <div>
                <HeaderRenderer
                    exportExcelParams={propertyLicenceEventsExportExcel(
                        this.state.propertyLicenceEvents,
                        propertyLicenceId,
                        this.state.propertyLicenceTypeId
                    )}
                    filters={PropertyLicenceEventsFilters}
                    filtersProps={filtersProps}
                    loading={loading}
                />
                <div className='clearfix'/>
                <Segment basic className='--table'>
                    <PropertyLicenceEventsTable
                        loading={loading}
                        propertyLicenceEvents={this.state.propertyLicenceEvents}
                        propertyLicenceTypeId={this.state.propertyLicenceTypeId}
                    />
                </Segment>
            </div>
        );
    }
}

export const PropertyLicenceEventsFiltersQuery = gql(propertyLicenceEventsGraphQL.propertyLicenceEventsFiltersQuery);
export const PropertyLicenceEventsQuery = gql(propertyLicenceEventsGraphQL.propertyLicenceEventsQuery);

const PropertyLicenceEventsWithQuery = compose(
    graphql(PropertyLicenceEventsFiltersQuery, {
        name: 'DataPropertyLicenceEventsFilters',
        options: (props) => {
            return {
                fetchPolicy: 'cache-first',
                notifyOnNetworkStatusChange: true,
                variables: {
                    propertyLicence: parseInt(props.match.params.id, 10),
                },
            };
        },
    }),
    graphql(PropertyLicenceEventsQuery, {
        name: 'DataPropertyLicenceEvents',
        skip: props => _isNull(props.propertyLicenceTypeId) || skipSearch(props),
        options: (props) => {
            let defaultFilters = PropertyLicenceEvents.defaultProps.filters,
                filters = props.filters,
                contentCategoryLevel3 = filters.contentCategoriesLevel3 || defaultFilters.contentCategoriesLevel3,
                tournaments = filters.tournaments || defaultFilters.tournaments;

            if (props.propertyLicenceTypeId !== PROPERTY_LICENCE_TYPE_OTHER) {
                contentCategoryLevel3 = defaultFilters.contentCategoriesLevel3;
            }

            if (props.propertyLicenceTypeId !== PROPERTY_LICENCE_TYPE_SPORTRADAR) {
                tournaments = defaultFilters.tournaments;
            }

            return {
                fetchPolicy: 'no-cache',
                notifyOnNetworkStatusChange: true,
                variables: {
                    search: filters.search || defaultFilters.search,
                    contentCategoryLevel3: contentCategoryLevel3,
                    eventStatus: filters.eventStatus || defaultFilters.eventStatus,
                    from: filters.from ? `${filters.from}:00` : `${defaultFilters.from}:00`,
                    reportType: filters.reportTypes || defaultFilters.reportTypes,
                    product: filters.products || defaultFilters.products,
                    propertyLicence: parseInt(props.match.params.id, 10),
                    to: !_isUndefined(filters.to) ? `${filters.to}:59` : null,
                    tournament: tournaments,
                    incidentType: filters.incidentTypes || defaultFilters.incidentTypes,
                    incidentSource: filters.incidentSources || defaultFilters.incidentSources,
                },
            };
        },
    })
)(FilterQueryWrapper(PropertyLicenceEvents, {
    queryForRefresh: 'DataPropertyLicenceEvents',
    filterUrls: ['propertyLicences.events.index'],
}));

const mapStateToProps = (state) => {
    let propertyLicence = state.app.entities.propertyLicence;

    return {
        filters: getSearchFilters(state, 'PropertyLicenceEvents', PropertyLicenceEvents.defaultProps.filters),
        propertyLicenceTypeId: (propertyLicence) ? parseInt(propertyLicence.property_licence_type_id, 10) : null,
    };
};

const mapDispatchToProps = mapModulesToProps(['Menu']);

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(
    FilterUrlParamsWrapper(PropertyLicenceEventsWithQuery, PropertyLicenceEvents.defaultProps.filters)
));
